import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  InputAdornment
} from '@mui/material';
import {
  Phone as PhoneIcon,
  ArrowForward as ArrowForwardIcon,
  MailOutline as MailIcon
} from '@mui/icons-material';
import axios from 'axios';

interface ForgotPasswordProps {
  onBackToLogin?: () => void;
}

const ForgotPassword: React.FC<ForgotPasswordProps> = ({ onBackToLogin }) => {
  const [twilioNumber, setTwilioNumber] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setSuccess(false); setLoading(true);
    try {
      await axios.post('/api/auth/forgot-password', { twilioNumber });
      setSuccess(true); setTwilioNumber('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'If an account exists with this number, a recovery email has been sent.');
    } finally { setLoading(false); }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: '#f6f6f8', px: 2 }}>
      {/* Logo */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Box sx={{ width: 56, height: 56, borderRadius: 2, bgcolor: '#4b2bee', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
          <MailIcon sx={{ color: '#fff', fontSize: 28 }} />
        </Box>
        <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: '1.75rem', color: '#0f172a', mb: 0.5 }}>
          Password Recovery
        </Typography>
        <Typography sx={{ fontFamily: 'Inter, sans-serif', color: '#475569', fontSize: '0.875rem' }}>
          Enter your phone number to receive recovery instructions
        </Typography>
      </Box>

      {/* Card */}
      <Box sx={{ width: '100%', maxWidth: 400, bgcolor: '#ffffff', borderRadius: 3, p: 4, boxShadow: '0px 12px 32px rgba(15, 23, 42, 0.04)' }}>
        {error && <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>{error}</Alert>}
        {success && (
          <Alert severity="success" sx={{ mb: 2.5, borderRadius: 2 }}>
            If an account exists with this number, a recovery email has been sent. Check your inbox.
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, color: '#475569', fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.8 }}>
            Twilio Number
          </Typography>
          <TextField
            fullWidth value={twilioNumber} onChange={(e) => setTwilioNumber(e.target.value)}
            placeholder="+1234567890" required disabled={success} autoFocus
            sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: 2, '& fieldset': { borderColor: 'rgba(226,232,240,0.5)' }, '&.Mui-focused fieldset': { borderColor: '#4b2bee', boxShadow: '0 0 0 3px rgba(75,43,238,0.1)' } } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ color: '#94a3b8', fontSize: 20 }} /></InputAdornment> }}
          />

          <Button type="submit" fullWidth variant="contained" disabled={loading || success} endIcon={!loading && <ArrowForwardIcon />}
            sx={{ bgcolor: '#4b2bee', borderRadius: 2, py: 1.5, textTransform: 'none', fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '0.9375rem', boxShadow: '0 4px 12px rgba(75,43,238,0.25)', '&:hover': { bgcolor: '#3d1fd4' }, '&.Mui-disabled': { bgcolor: '#e2e8f0' } }}
          >
            {loading ? 'Sending...' : 'Send Recovery Email'}
          </Button>
        </Box>

        <Typography sx={{ textAlign: 'center', mt: 3, fontFamily: 'Inter, sans-serif', color: '#475569', fontSize: '0.875rem' }}>
          <Link component="button" onClick={(e: React.MouseEvent) => { e.preventDefault(); onBackToLogin?.(); }} type="button"
            sx={{ color: '#4b2bee', textDecoration: 'none', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
          >
            ← Back to Login
          </Link>
        </Typography>
      </Box>
    </Box>
  );
};

export default ForgotPassword;
